/**
 * C1 v1.5: the Config tab reads and writes (C2 v1.7 `GET /api/config` and the six `PUT` / `DELETE` routes) and every
 * sentence of the tab. Types mirror C2's JSON field for field; a value is the API's string and is never computed with
 * here. Nothing is defaulted: a value Damien has not set is null with the sentence C2 hands back (`not_set`).
 * C1 v1.20: the Serving sizes pane (C2 v1.21 `serving_sizes` and `PUT` / `DELETE /api/config/serving-sizes`, ruling S-F).
 * C1 v1.49 (c1-currency-links, G6): the currency links name the dirham's peg source (C7 v1.13), and C2 v1.51's `unmapped` reads
 * as a banner and one row per currency with no stored rate. Only the total for the banner is added up here; every count is C2's.
 * C1 v1.62 (c1-margin-move-owner, T12b): C2 v1.59's `weekly_move_owner`, one email, set and cleared here; absent on an older API.
 */
import { PricingError, type Channel, type Grid, type GridRow, type Market } from "./api";
import { channelLabel, COPY, costFx, detailPlace, hasCost, MARKET_LABEL, personName } from "./format";

export type GlobalKey = "margin_target_pct" | "weekly_move_threshold_pts" | "stale_basis_hours";
export type SettingKey = GlobalKey | "rrp_anchor_market" | "weekly_move_owner";
export type AnchorValue = { marketplace_id: string; marketplace_name: string | null };
/** One setting as C2 v1.7 serves it; `value` is a decimal string for the three company keys, an anchor object for the per-market key,
 * an email for the owner key (C2 v1.59). */
export type Setting = {
  key: SettingKey; scope: string; unit: string; value: string | AnchorValue | null; set_by: string | null; set_at: string | null;
  effective_from: string | null; note: string | null; not_set: string | null; rrps_retired?: number; retired?: number;
};
export type RrpProduct = {
  tracking: string; variant: string; active: boolean; rrp: string | null; currency: string | null; anchor_market: string | null;
  note: string | null; set_by: string | null; set_at: string | null; effective_from: string | null;
};
export type RrpRegion = { anchor: Setting; products: RrpProduct[]; set_count: number; product_count: number };
export type FloorRow = {
  xero_sku: string; product: string; variant: string; channel: string; channel_location: string; market: Market; active: boolean;
  floor: string | null; currency: string | null; source: string | null; effective_date: string | null; note: string | null;
  set_by: string | null; set_at: string | null; retired?: number;
};
/** One registry product's serving size as C2 v1.21 serves it: decimal strings (units a whole number), null when not set. */
export type ServingProduct = {
  tracking: string; variant: string; active?: boolean; servings_per_pack: string | null; max_daily_servings: string | null;
  units_per_pack: number | null; note: string | null; set_by: string | null; set_at: string | null; retired?: number;
};
export type ServingSizes = { products: ServingProduct[]; set_count: number; product_count: number };
export type CurrencyLink = { market: Market; channel: string; native_currency: string; fx_source: string; entity_id: string };
/** A currency some pricing input is held in with no stored rate (C2 v1.51): its count of COGS entries, fee components and committed prices. */
export type UnmappedCurrency = { currency: string; cogs: number; fees: number; prices: number; total: number };
export type ConfigRead = {
  settings: Record<GlobalKey, Setting> & { weekly_move_owner?: Setting }; rrp: Record<Market, RrpRegion>; floors: FloorRow[]; currency_links: CurrencyLink[];
  serving_sizes: ServingSizes;
  /** C2 v1.51; absent on an older answer, which reads as nothing unmapped. */
  unmapped?: UnmappedCurrency[];
  /** C2 v1.71 (DEC-1367 C): per market, the price ending's direction and cents, each set or not; absent on an older answer. */
  price_endings?: Record<Market, { direction: Pick<Setting, "value" | "not_set">; ending: Pick<Setting, "value" | "not_set"> }>;
};
export type RrpWrite = { tracking: string; variant: string; market: Market; rrp: string | null; currency: string | null; anchor_market: string | null; note: string | null; set_by: string | null; set_at: string | null; effective_from: string | null; retired?: number };

export const MARKETS: Market[] = ["UK", "EU", "AE"];
export const STALE_HOURS = ["12", "24", "48"] as const;
/** The Amazon marketplaces of migration 006 that trade in each market's currency: the only anchors C3 v1.1 accepts. */
export const ANCHOR_OPTIONS: Record<Market, Array<{ id: string; name: string }>> = {
  UK: [{ id: "A1F83G8C2ARO7P", name: "Amazon.co.uk" }],
  EU: [
    { id: "A1PA6795UKMFR9", name: "Amazon.de" }, { id: "A13V1IB3VIYZZH", name: "Amazon.fr" }, { id: "APJ6JRA9NG5V4", name: "Amazon.it" },
    { id: "A1RKKUPIHCS9HS", name: "Amazon.es" }, { id: "A1805IZSGTT6HS", name: "Amazon.nl" }, { id: "AMEN7PMS3EDWL", name: "Amazon.com.be" },
    { id: "A28R8C7NBKEWEA", name: "Amazon.ie" },
  ],
  AE: [{ id: "A2VIGQ35RCS4UG", name: "Amazon.ae" }],
};
export const FX_SOURCE_LABEL: Record<string, string> = {
  ECB: "ECB daily reference", ECB_USD_AED_PEG: "ECB US dollar rate × UAE peg 3.6725",
};
export const FX_NATIVE_LABEL = "Native (no conversion)";
export const ENTITY_LABEL: Record<string, string> = { LTD: "Ketosource Ltd", OU: "Ketosource OÜ", LLC: "Ketosource Trading LLC" };
/** Floors are typed in Config (owner ruling Q12, 2026-09-29: the legacy MAP tab is not imported as floors). The engine's `MAP`
 * code names a row the legacy importer would write; on screen it is the legacy sheet, never "MAP", which is not a lawful term in
 * the UK or EU (CL-033, bug 4010402f-c981-ea96). */
export const FLOOR_SOURCE_LABEL: Record<string, string> = { operator: "Set in Config", legacy_sheet: "Legacy sheet", MAP: "Legacy sheet" };
/** A floor's source in words; a code this screen does not know reads as an imported floor, never as the raw code. */
export const floorSourceLabel = (source: string | null | undefined): string => FLOOR_SOURCE_LABEL[source ?? ""] ?? "Imported";

export const CONFIG_COPY = {
  title: "Config",
  desc: "The only place the margin target, weekly-move threshold and owner, RRP anchors, own-channel floors, serving sizes and currency links are set.",
  save: "Save", remove: "Remove", saving: "Saving…", saved: "Saved", removed: "Removed",
  enterNumber: "Enter a number first.",
  target: {
    title: "Margin target", label: "Gross margin target", unit: "% GM",
    desc: "One gross margin target. Overview, the Price Run Below target flag and the SKU drawer read it from here.",
  },
  move: {
    title: "Weekly-move threshold", label: "Threshold", unit: "percentage points",
    desc: "A week-on-week margin move larger than this sits at the top of Overview with Record action.",
  },
  rrp: {
    title: "RRP anchors",
    desc: "One RRP per product per region: it follows the price committed on the anchor market (the first anchor commit sets it), and a value typed here holds until the next anchor commit. A price below it on a row the RRP rule covers is an Amazon parity risk and needs an exception to commit; the Price Run states the rule and marks those rows.",
    columns: ["Region", "Anchor market", "RRPs", ""],
    anchorLabel: "Anchor market", pickAnchor: "Choose the anchor market", noAnchor: "Not set",
    anchorFirst: (region: string) => `Set the anchor market before an RRP for ${region}.`,
    products: (n: number, total: number) => `${n} of ${total} products`,
    list: (items: string) => `RRPs set: ${items}`,
    noneSet: (region: string) => `Price Run rows read No RRP set for ${region}`,
    retired: (n: number) => (n === 1 ? "1 RRP was retired: re-enter it from the new anchor." : `${n} RRPs were retired: re-enter them from the new anchor.`),
    productColumns: ["Product", "RRP", "Set by", ""],
    inactive: "no active SKU", edit: "Edit", close: "Close", notSet: "Not set",
  },
  floors: {
    title: "Own-channel floors",
    desc: "A staged price below its floor needs a recorded reason to save and to commit. Floors are set here, one per SKU; the Price Run shows each in its row and its drawer.",
    columns: ["SKU / Channel", "Floor", "Source", ""],
    addTitle: "Add a floor", skuLabel: "Xero SKU", floorLabel: "Floor", add: "Add floor", enterSku: "Enter a Xero SKU first.",
    none: "No floors yet. Add one below.",
    unknownSku: (sku: string) => `${sku} is not in the SKU registry.`,
  },
  serving: {
    title: "Serving sizes",
    desc: "Servings in one pack, the most servings a day and, if you want a per-unit price, the units in one pack. One row per product, for every market; a case SKU counts every pack it holds. The Price Run's Customer column and drawer read price per serving, per day, per month (30 days) and per unit from here.",
    columns: ["Product", "Servings per pack", "Max servings a day", "Units per pack", ""],
    products: (n: number, total: number) => `${n} of ${total} products set`,
    servingsLabel: "Servings per pack", maxLabel: "Max servings a day", unitsLabel: "Units per pack",
    enterServings: "Enter the servings per pack first.", wholeUnits: "Units per pack is a whole number.",
    optional: "optional",
  },
  links: {
    title: "Currency links",
    desc: "Each market’s native currency and the FX source used to convert costs. A link reads Native (no conversion) when the last daily calculation converted none of its costs. A cost, fee or price in a currency with no stored rate is left out of margins until a rate source is added. Links are read-only: each currency has one rate source.",
    columns: ["Market", "Native currency", "FX source"],
    unmappedBanner: (n: number) => (n === 1
      ? "1 pricing input is in a currency with no stored rate, so the rows it touches are left out of margins."
      : `${n} pricing inputs are in a currency with no stored rate, so the rows they touch are left out of margins.`),
    unmapped: "unmapped",
    notSet: "Not set",
    report: "Report it as a bug so a rate source is added.",
  },
  owner: {
    title: "Weekly-move owner", label: "Owner", placeholder: "name@ketosource.co",
    desc: "The person who acts on a flagged weekly margin move. Overview names them beside the moves that still need an action; anyone with edit rights may record the action.",
    enterEmail: "Enter one email address first.",
  },
  stale: {
    title: "Price-run settings", desc: "The Price Run and Overview flag the daily figures once they are older than this, with two hours’ grace for a late run. The weekly Xero stock read keeps its own 8-day flag.", label: "Warn when the snapshot is older than",
    option: (h: string) => `${h} hours`,
  },
  setBy: (setBy: string | null, setAt: string | null, humanStamp: (iso: string | null) => string) =>
    setBy ? `Set by ${personName(setBy)} · ${humanStamp(setAt)}` : "",
  regionLabel: (m: Market) => MARKET_LABEL[m],
};

const BASE = import.meta.env.VITE_PRICING_API_BASE ?? "/precision_pricing/api";
const HEADERS = { Accept: "application/json", "Content-Type": "application/json" };
async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  // Same shape as api.ts `call`: the cookie rides on credentials; no-store because a save on another tab must show on the next read.
  const response = await fetch(`${BASE}${path}`, { method: "GET", credentials: "include", cache: "no-store", headers: HEADERS, ...init });
  if (!response.ok) {
    let body: { error?: { reason?: string; message?: string } } | null = null;
    try { body = await response.json() as typeof body; } catch { body = null; }
    throw new PricingError(response.status, body);
  }
  return await response.json() as T;
}
const q = (params: Record<string, string | undefined>) => {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) s.set(k, v);
  const text = s.toString();
  return text ? `?${text}` : "";
};
export const getConfig = () => call<ConfigRead>("/config");
export const putSetting = (key: SettingKey, body: { value: string; scope?: string; note?: string }) =>
  call<Setting>(`/config/settings/${key}`, { method: "PUT", body: JSON.stringify(body) });
export const deleteSetting = (key: SettingKey, scope?: string) => call<Setting>(`/config/settings/${key}${q({ scope })}`, { method: "DELETE" });
export const putRrp = (body: { market: Market; tracking: string; variant: string; rrp: string; note?: string }) =>
  call<RrpWrite>("/config/rrp", { method: "PUT", body: JSON.stringify(body) });
export const deleteRrp = (market: Market, tracking: string, variant: string) => call<RrpWrite>(`/config/rrp${q({ market, tracking, variant })}`, { method: "DELETE" });
export const putFloor = (sku: string, body: { floor: string; note?: string }) => call<FloorRow>(`/config/floors/${encodeURIComponent(sku)}`, { method: "PUT", body: JSON.stringify(body) });
export const putServing = (body: { tracking: string; variant: string; servings_per_pack: string; max_daily_servings: string | null; units_per_pack: string | null; note?: string }) =>
  call<ServingProduct>("/config/serving-sizes", { method: "PUT", body: JSON.stringify(body) });
export const deleteServing = (tracking: string, variant: string) => call<ServingProduct>(`/config/serving-sizes${q({ tracking, variant })}`, { method: "DELETE" });
export const deleteFloor = (sku: string) => call<FloorRow>(`/config/floors/${encodeURIComponent(sku)}`, { method: "DELETE" });

export const CONFIG_KEY = ["pricing", "config"] as const;
/** The whole tab in one read. Never stale: a save on another tab must show on the next focus. */
export function configQuery() {
  return { queryKey: CONFIG_KEY, queryFn: getConfig, retry: 1, staleTime: 0 };
}

/** A decimal string the API accepts, or null: digits with one optional point, no sign, no letters (C2 takes strings only). */
export function cleanNumber(raw: string): string | null {
  const t = raw.trim().replace(/,/g, ".");
  return /^\d+(\.\d{1,4})?$/.test(t) ? t : null;
}
/** The sentence for a refused write: the store's own sentence on a 409, the access sentence on a 403, else the unreachable line. */
export function writeRefusal(error: unknown, areaName: string): string {
  if (error instanceof PricingError) {
    const facts = error.facts();
    if (error.status === 409 && facts.message) return facts.message.replace(/^pricing: /, "");
    if (error.status === 422 && facts.message) return facts.message;
    if (error.status === 404 && facts.message) return facts.message;
    if (error.status === 401 || error.status === 403) return COPY.forbidden(areaName);
  }
  return COPY.unreachable;
}
/** A decimal string above zero, or null: cleanNumber without an all-zero value (a string test; nothing is read as a number). */
export function cleanPositive(raw: string): string | null {
  const t = cleanNumber(raw);
  return t && !/^0+(\.0+)?$/.test(t) ? t : null;
}
/** A whole number of units the API accepts, or null. */
export function cleanWhole(raw: string): string | null {
  const t = raw.trim();
  return /^[1-9]\d*$/.test(t) ? t : null;
}
export const anchorValue = (s: Setting): AnchorValue | null => (s.value && typeof s.value === "object" ? s.value : null);
/** A SKU's active own-channel floor from the Config read: the row, null when none is set, undefined while Config is unread or
 * failed (C2 still checks the floor on save). A ship-country row asks with its store's SKU (`skuOf`). */
export function floorOf(floors: FloorRow[] | undefined, sku: string): FloorRow | null | undefined {
  return floors ? floors.find((f) => f.xero_sku === sku) ?? null : undefined;
}
/** Whether a price in `currency` can be compared with the floor: only in the floor's own currency. In any other currency C2
 * asks a reason for every save (`floor_not_comparable`), so the screen says so. A comparison of two codes, never of amounts. */
export function floorInCurrency(floor: FloorRow, currency: string | null): boolean {
  return currency === null || floor.currency === currency;
}
/** C2 v1.51's unmapped currencies; an answer older than v1.51 carries none. */
export function unmappedOf(read: ConfigRead): UnmappedCurrency[] {
  return read.unmapped ?? [];
}
/** The banner's count: every unmapped currency's total, the one sum this screen makes. */
export function unmappedTotal(read: ConfigRead): number {
  return unmappedOf(read).reduce((n, u) => n + u.total, 0);
}
/** One unmapped currency's inputs in words, the kinds it holds only ("3 COGS entries, 1 fee, 1 price"). */
export function unmappedInputs(u: UnmappedCurrency): string {
  const words: [number, string, string][] = [[u.cogs, "COGS entry", "COGS entries"], [u.fees, "fee", "fees"], [u.prices, "price", "prices"]];
  return words.filter(([n]) => n > 0).map(([n, one, many]) => `${n} ${n === 1 ? one : many}`).join(", ");
}
/** A currency link's FX source in words, from the data (v26 gap audit, Config conflict 6): Native when the market's last daily
 * grid converted none of that channel's costs and costed at least one of its rows, else the source the link names. Without a
 * grid (unread, failed or none yet), or with no costed row, the link's source: nothing says whether it would be used. */
export function fxSourceLabel(link: CurrencyLink, grid: Grid | undefined): string {
  const source = FX_SOURCE_LABEL[link.fx_source] ?? link.fx_source;
  const rows = grid ? grid.rows.filter((row) => row.channel === link.channel) : [];
  if (rows.some((row) => costFx(row) !== null) || !rows.some(hasCost)) return source;
  return FX_NATIVE_LABEL;
}
export const numberValue = (s: Setting): string | null => (typeof s.value === "string" ? s.value : null);
/** An owner value the API may accept, or null: trimmed and lower-cased, one `@`, no spaces. C2 judges the full shape and says why it refuses. */
export function cleanEmail(raw: string): string | null {
  const t = raw.trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+$/.test(t) ? t : null;
}
/** The entity a channel's Pool COGS come from (C1 v1.17): the currency link's `entity_id` as the Config tab names it; null with no link. */
export function entityOf(links: CurrencyLink[] | undefined, market: Market, channel: string): string | null {
  const link = links?.find((l) => l.market === market && l.channel === channel);
  return link ? ENTITY_LABEL[link.entity_id] ?? link.entity_id : null;
}
/** The drawer header's second line (C1 v1.17, v26 "Region · Entity"): the SKU, the place, the market and its entity when the Config read is on hand. */
export function detailSubtitle(row: GridRow, market: Market, links: CurrencyLink[] | undefined): string {
  const entity = entityOf(links, market, row.channel);
  return `${row.xero_sku} · ${detailPlace(row, market)} · ${MARKET_LABEL[market]}${entity ? ` · ${entity}` : ""}`;
}
/** The Price Run's market currency footer (C1 v1.17, v26 "Market currency: GBP (native) · ..."): one line per market on view
 * from its currency links, the FX label per channel as the Config tab reads it, and the entity the Pool COGS come from. Null
 * while the Config read is not on hand or names no link for the market. */
export function currencyFooter(links: CurrencyLink[] | undefined, market: Market, grid: Grid | undefined): string | null {
  const own = (links ?? []).filter((l) => l.market === market);
  if (own.length === 0) return null;
  const fx = (l: CurrencyLink) => `${channelLabel({ channel: l.channel as Channel })} ${fxSourceLabel(l, grid)}`;
  const same = own.every((l) => l.native_currency === own[0].native_currency && l.entity_id === own[0].entity_id);
  if (same) return `${COPY.marketCurrency(MARKET_LABEL[market])}: ${own[0].native_currency} · ${COPY.poolCogsFrom(ENTITY_LABEL[own[0].entity_id] ?? own[0].entity_id)} · ${own.map(fx).join(" · ")}`;
  return `${COPY.marketCurrency(MARKET_LABEL[market])} · ${own.map((l) => `${fx(l)}: ${l.native_currency}, ${COPY.poolCogsFrom(ENTITY_LABEL[l.entity_id] ?? l.entity_id)}`).join(" · ")}`;
}
